// Product JSON-LD of a product page, built by `get_product_JsonLd_server` (`@core/helpers/server/getJsonLds`). Pure: no
// alias imports, so it is unit-tested with node --test. Same approach as jument / chawk-bazar / ciseco
// `helpers/productJsonLd.js`: a product with variants is a `ProductGroup` (`hasVariant` / `variesBy` belong there, not on
// `Product`), the colour label is `color` or `colors` depending on the tenant, and prices follow the site currency
// (`product_meta_currency`, the same table as the `product:*` meta tags: toman x 10 = IRR).
import { product_meta_currency } from "./product-meta.js";

const VARIANT_DIMENSIONS = [
  { label_keys: ["color", "colors"], property: "color", schema_url: "https://schema.org/color" },
  { label_keys: ["size", "sizes"], property: "size", schema_url: "https://schema.org/size" },
];

/** `origin` + a page path with exactly one `/` between them; an absolute url is kept, the path's trailing slash as given. */
export const json_ld_absolute_url = (origin, path) => {
  if (typeof path !== "string") return undefined;
  const value = path.trim();
  if (!value || value === "#") return undefined;
  if (/^https?:\/\//i.test(value)) return value;
  return `${String(origin || "").replace(/\/+$/, "")}/${value.replace(/^\/+/, "")}`;
};

/** A storage media path under the storage prefix (one `/`); undefined without a path. */
export const json_ld_media_url = (storage_prefix_url, url) => {
  if (typeof url !== "string") return undefined;
  const value = url.trim();
  if (!value) return undefined;
  if (/^https?:\/\//i.test(value)) return value;
  return `${String(storage_prefix_url || "").replace(/\/+$/, "")}/${value.replace(/^\/+/, "")}`;
};

const gallery_urls = (storage_prefix_url, gallery) =>
  Array.isArray(gallery) ? gallery.map((media) => json_ld_media_url(storage_prefix_url, media?.url)).filter(Boolean) : undefined;

/**
 * The path of a record's page: `seo.full_url`, else `seo.url` when it is a path, else `fallback_path` (the requested
 * path, for the page being rendered). A bare slug in `seo.url` is not a page address, so it is never used.
 */
export const json_ld_page_path = (seo, fallback_path) => {
  const candidates = [seo?.full_url, seo?.url, fallback_path];
  for (const candidate of candidates) {
    if (typeof candidate !== "string") continue;
    const value = candidate.trim();
    if (/^https?:\/\//i.test(value)) return value;
    if (candidate === seo?.full_url && value && value !== "#") return value;
    if (value.startsWith("/") && !value.startsWith("//")) return value;
  }
  return undefined;
};

const label_of = (item, label_keys) => {
  for (const key of label_keys) {
    const label = item?.labels_obj?.[key];
    if (label) return label;
  }
  return undefined;
};

/** Only the dimensions whose title really differs between the variants. */
const varying_dimensions = (mix_variant) =>
  VARIANT_DIMENSIONS.filter(({ label_keys }) => {
    const titles = new Set(mix_variant.map((item) => label_of(item, label_keys)?.title).filter(Boolean));
    return titles.size > 1;
  });

/**
 * @param {object} args
 * @param {object} args.product              `getProductBySlug` data
 * @param {string} [args.origin]             e.g. `https://shop.example.com`
 * @param {string} [args.storage_prefix_url] image CDN prefix
 * @param {string|number} [args.web_currency] site currency setting (1 toman, 2 rial, 3 dollar; default rial)
 * @param {string} [args.request_path]       path of the page being rendered, used when the product has no `seo.full_url`
 * @returns {object} `Product` (no variant or one) or `ProductGroup` (two or more variants)
 */
export const build_product_json_ld = ({ product, origin, storage_prefix_url, web_currency, request_path } = {}) => {
  const { currency, multiplier } = product_meta_currency(web_currency);
  // rial keeps the backend value as it was; toman/dollar convert like the `product:*` meta tags
  const amount = (value) =>
    multiplier === 1 || value === null || value === undefined || value === "" || !Number.isFinite(Number(value))
      ? value
      : Number(value) * multiplier;
  const offer = (item) => ({
    "@type": "Offer",
    priceCurrency: currency,
    price: amount(item?.price?.main_price),
    availability: `https://schema.org/${item?.details?.count > 0 && item?.price?.price > 0 ? "InStock" : "OutOfStock"}`,
  });

  const mix_variant = Array.isArray(product?.mix_variant) ? product.mix_variant.filter(Boolean) : [];
  const is_group = mix_variant.length > 1;

  const obj = {
    "@context": "https://schema.org",
    "@type": is_group ? "ProductGroup" : "Product",
    name: product?.title,
    image: gallery_urls(storage_prefix_url, product?.media_gallery),
    description: product?.description,
    url: json_ld_absolute_url(origin, json_ld_page_path(product?.seo, request_path)),
  };
  if (product?.brand?.title) obj.brand = { "@type": "Brand", name: product.brand.title };

  if (!is_group) {
    // a product without variants, or with only the default one: a plain Product with its own offer
    const source = mix_variant[0] || product;
    if (mix_variant[0]?.details?.product_code) obj.sku = mix_variant[0].details.product_code;
    obj.offers = offer(source);
    return obj;
  }

  const dimensions = varying_dimensions(mix_variant);
  const group_id = product?.id ?? product?.details?.product_code;
  if (group_id !== undefined && group_id !== null && group_id !== "") obj.productGroupID = String(group_id);
  if (dimensions.length > 0) obj.variesBy = dimensions.map(({ schema_url }) => schema_url);
  obj.hasVariant = mix_variant.map((item) => {
    const variant = {
      "@type": "Product",
      sku: item?.details?.product_code,
      image: gallery_urls(storage_prefix_url, item?.media_gallery),
      name: [product?.title, item?.title].filter(Boolean).join(" "),
    };
    for (const { label_keys, property } of VARIANT_DIMENSIONS) {
      const title = label_of(item, label_keys)?.title;
      if (title) variant[property] = title;
    }
    variant.offers = offer(item);
    return variant;
  });
  return obj;
};
